import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#050505]"
    >
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <Logo />
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-8 md:p-10">
          <div className="label-eyebrow mb-3">Admin Portal</div>
          <h1
            className="font-display text-3xl text-white mb-2"
            style={{ fontWeight: 400 }}
          >
            Sign in to continue
          </h1>
          <p className="text-sm text-white/50 mb-8">
            Restricted area — credentials required.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
                Email
              </span>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                />
                <input
                  data-testid="admin-login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#050505] border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none transition-colors"
                  placeholder="admin@afmco.com.au"
                  required
                />
              </div>
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
                Password
              </span>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                />
                <input
                  data-testid="admin-login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#050505] border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                />
              </div>
            </label>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black font-medium py-3.5 hover:bg-[#F6ECCC] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-white/30 mt-8">
          © {new Date().getFullYear()} Keys to Own — Admin Console
        </p>
      </div>
    </div>
  );
}
